import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';

import { type DynamicContent, type StaticRoute } from '@/utils/searchHelper';

import { useSearchContent } from './useSearchContent';

// Re-export types for compatibility
export type { StaticRoute, DynamicContent } from '@/utils/searchHelper';

export interface SearchResultItem {
  id?: string;
  label: string;
  route: string;
  type: 'static' | 'dynamic';
  category?: string;
  description?: string;
}

interface UseSearchOptions {
  maxStaticResults?: number;
  maxDynamicResults?: number;
  debounceMs?: number;
  autoNavigate?: boolean;
}

interface UseSearchReturn {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  results: { static: StaticRoute[]; dynamic: DynamicContent[] };
  isLoading: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  totalResults: number;
  handleResultSelect: (result: StaticRoute | DynamicContent, type: 'static' | 'dynamic') => void;
  handleKeyNavigation: (e: React.KeyboardEvent) => void;
  clearSearch: () => void;
  performSearch: (term: string) => Promise<void>;
}

/**
 * Custom hook for search functionality
 * Provides search state management and keyboard navigation
 * Now uses GraphQL for dynamic content search
 */
export const useSearch = (options: UseSearchOptions = {}): UseSearchReturn => {
  const {
    maxStaticResults = 5,
    maxDynamicResults = 5,
    debounceMs = 300,
    autoNavigate = true,
  } = options;

  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Use the new search content hook
  const {
    searchTerm,
    setSearchTerm: setSearchTermInternal,
    results,
    isLoading,
    totalResults,
    performSearch,
  } = useSearchContent({
    maxStaticResults,
    maxDynamicResults,
    debounceMs,
  });

  // Custom setSearchTerm that also manages the dropdown
  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchTermInternal(term);
      setIsOpen(term.length > 0);
      setSelectedIndex(-1);
    },
    [setSearchTermInternal]
  );

  // Handle result selection
  const handleResultSelect = useCallback(
    (result: StaticRoute | DynamicContent, type: 'static' | 'dynamic') => {
      const searchResult: SearchResultItem = {
        ...result,
        type,
        label: 'title' in result ? result.title : result.label,
        route: 'route' in result ? result.route : result.url,
      };

      if (autoNavigate) {
        const url = 'route' in result ? result.route : result.url;
        router.push(url);
      }

      // Close search and clear
      setIsOpen(false);
      setSearchTerm('');

      return searchResult;
    },
    [router, autoNavigate, setSearchTerm]
  );

  // Handle keyboard navigation
  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < totalResults - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < results.static.length) {
            handleResultSelect(results.static[selectedIndex], 'static');
          } else {
            const dynamicIndex = selectedIndex - results.static.length;
            handleResultSelect(results.dynamic[dynamicIndex], 'dynamic');
          }
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    },
    [totalResults, selectedIndex, results, handleResultSelect]
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(-1);
  }, [setSearchTerm]);

  return {
    searchTerm,
    setSearchTerm,
    results,
    isLoading,
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex,
    totalResults,
    handleResultSelect,
    handleKeyNavigation,
    clearSearch,
    performSearch,
  };
};
